import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

import { SocketService } from './socket.service';

/** Tracks which phase of a two-phase upload is active:
 *  'http'  = browser → server  (XHR progress)
 *  's3'    = server  → S3      (socket progress)
 */
export type UploadPhase = 'http' | 's3' | null;

@Injectable({
  providedIn: 'root',
})
export class LoaderService {
  // subscription to track loader status
  isLoading = new Subject<boolean>();

  // show progress for uploading content
  isShowProgress = new Subject<boolean>();

  // show progress percentage for uploading content (S3 phase — fed by socket)
  progressPercentage = new Subject<number>();

  /**
   * HTTP upload progress (browser → server phase).
   * Values 0–100. Pushed by the component directly from HttpEventType.UploadProgress.
   */
  httpUploadProgress = new BehaviorSubject<number>(0);

  /**
   * Which phase of the upload is active so the overlay can show the right label.
   * null  = not uploading
   * 'http' = sending file from browser to server
   * 's3'   = server streaming file to S3
   */
  uploadPhase = new BehaviorSubject<UploadPhase>(null);

  private uploadContentProgress!: Subscription;

  constructor(private socketService: SocketService) {}

  /**
   * Method for show loader.
   */
  show(): void {
    this.isLoading.next(true);
  }

  /**
   * Method for hide loader.
   */
  hide(): void {
    this.isLoading.next(false);
  }

  /**
   * Push an HTTP upload progress value (0–100) and switch phase to 'http'.
   * Maps 0–100% HTTP to 0–50% on progressPercentage.
   */
  setHttpUploadProgress(percent: number): void {
    this.uploadPhase.next('http');
    this.httpUploadProgress.next(percent);
    const combined = Math.round(percent * 0.5);
    this.progressPercentage.next(combined);
  }

  setSocketForUploadContent(socketUUID: string) {
    localStorage.setItem('socketUUID', socketUUID);
    // Switch phase to s3 as soon as first socket event arrives
    this.uploadPhase.next('s3');
    this.uploadContentProgress = this.socketService.on(socketUUID).subscribe({
      next: (res) => {
        const progress = Number(res);
        if (progress < 100) {
          localStorage.setItem('uploadProgress', progress.toString());
          // Maps 0–100% S3 progress to 50–100% on progressPercentage
          const combined = Math.round(50 + progress * 0.5);
          this.progressPercentage.next(combined);
        } else {
          this.progressPercentage.next(100);
          this.hideProgress();
          this.destroyProgress();
        }
      },
      error: (err) => {
        console.error('Error in WebSocket subscription', err);
      },
    });
  }

  destroyProgress() {
    if (this.uploadContentProgress) {
      this.uploadContentProgress.unsubscribe();
    }
    this.uploadPhase.next(null);
    this.httpUploadProgress.next(0);
    localStorage.removeItem('socketUUID');
    localStorage.removeItem('uploadProgress');
  }

  /**
   * Method for show progress
   */
  showProgress(): void {
    this.isShowProgress.next(true);
  }

  /**
   * Method for hide progress
   */
  hideProgress(): void {
    this.isShowProgress.next(false);
  }
}

